<template>
    <div class="login-page">
        <section class="login-hero">
            <div class="hero-content">
                <div class="hero-brand">
                    <img src="@/assets/imolgaro.png" class="brand-logo" />
                    <div class="hero-badge">SGOS WEB</div>
                </div>

                <h1>Sistema de Gestão para Ordens de Serviço</h1>

                <p>
                    Controle ordens de serviço, frota, manutenção, gabinete,
                    obras e operações municipais em um único ambiente.
                </p>

                <div class="hero-list">
                    <span>✓ Gestão operacional</span>
                    <span>✓ Controle por perfil de acesso</span>
                    <span>✓ Acompanhamento em tempo real</span>
                </div>
            </div>
        </section>

        <section class="login-area">
            <div class="login-card">
                <div class="login-header">
                    <h2>Acesse sua conta</h2>
                    <p>Informe suas credenciais para continuar.</p>
                </div>

                <form @submit.prevent="loginNew">
                    <div class="input-group row-space">
                        <img src="@/assets/icons/mail.svg" alt="E-mail" class="icon" />
                        <input
                            type="text"
                            placeholder="Digite seu login"
                            v-model="credenciais.email"
                            autocomplete="username"
                        />
                    </div>

                    <div class="input-group">
                        <img src="@/assets/icons/pass.svg" alt="Senha" class="icon" />
                        <input
                            type="password"
                            placeholder="Digite sua senha"
                            v-model="credenciais.passWord"
                            autocomplete="current-password"
                        />
                    </div>

                    <div class="robot-check">
                        <NotRobotCheck v-model="notRobot" />
                    </div>

                    <div v-if="errorMessage" class="error-message">
                        {{ errorMessage }}
                    </div>

                    <button class="login-button" :disabled="isLoading">
                        <span v-if="isLoading" class="loader"></span>
                        {{ isLoading ? 'Autenticando...' : 'Acessar sistema' }}
                    </button>
                </form>

                <div class="login-footer">
                    <span>iMolgaro Engenharia de Sistemas</span>
                    <small>{{ globalStore.appname }} · v{{ globalStore.appver }}</small>
                </div>
            </div>
        </section>
    </div>
</template>


<script setup>
import { reactive, ref } from 'vue';
import { useRouter } from 'vue-router';

import { genericPost, getCadastroById, loginAPI } from '@/funcoes/api';
import { globalStore } from '@/globals';
import { useLoginStore } from '@/store/login';
import NotRobotCheck from '@/components/NotRobotCheck.vue';

defineOptions({
    name: 'LoginPage'
});

const router = useRouter();
const loginStore = useLoginStore();

const credenciais = reactive({
    email: '',
    passWord: ''
});

const isLoading = ref(false);
const errorMessage = ref('');
const notRobot = ref(false);

const loginNew = async () => {
    if (!credenciais.email || !credenciais.passWord) {
        errorMessage.value = 'Informe o usuário/e-mail e a senha.';
        return;
    }

    if (!notRobot.value) {
        errorMessage.value = 'Por favor, confirme que você não é um robô.';
        return;
    }

    try {
        isLoading.value = true;
        errorMessage.value = '';

        const dados = {
            appname: globalStore.appname,
            appver: globalStore.appver,
            login: credenciais.email,
            senha: credenciais.passWord
        };

        const loginSuccess = await loginAPI(
            credenciais.email,
            credenciais.passWord,
            dados
        );

        if (!loginSuccess) {
            errorMessage.value = 'Login não autorizado.';
            return;
        }

        const req = await genericPost('/main/registrarlogin', dados);

        if (!req) {
            errorMessage.value = 'Não foi possível registrar o login.';
            return;
        }

        const usuario = await getCadastroById('usuario', req.idusuario);

        if (Array.isArray(usuario) && usuario.length > 0) {
            globalStore.ckadmin = usuario[0].ckadmin;
            globalStore.ckipprestador = usuario[0].ckipprestador;
        }

        globalStore.idlogin = req.id;
        globalStore.idipprestador = req.idfornecedor;
        globalStore.idmunicipio = req.idmunicipio;
        globalStore.idusuario = req.idusuario;
        globalStore.nmusuario = req.nmusuario;
        globalStore.nmmunicipio = req.nmmunicipio;
        globalStore.nmipprestador = req.nmfornecedor;

        const perfil = await getCadastroById('usuario_perfil', req.idusuario);

        if (Array.isArray(perfil) && perfil.length > 0) {
            globalStore.perfil = perfil[0];
        }

        const config = await getCadastroById('municipio_config', req.idmunicipio);

        if (Array.isArray(config) && config.length > 0) {
            globalStore.ckpermitir_parcelar = config[0].ckpermitir_parcelar;
            globalStore.cobr_prazo_inicial = config[0].cobr_prazo_inicial;
            globalStore.latitude = config[0].latitude;
            globalStore.longitude = config[0].longitude;
        }

        loginStore.setLogin(req);

        if (req.idfornecedor) {
            router.push('/main-ipprestador');
            return;
        }

        if (globalStore.perfil?.frota_acesso === 1) {
            router.push('/main-frota');
        } else if (globalStore.perfil?.gab_acesso === 1) {
            router.push('/main-gabinete');
        } else if (globalStore.perfil?.agr_acesso === 1) {
            router.push('/main-os');
        } else if (globalStore.perfil?.ip_acesso === 1) {
            router.push('/main-ip');
        } else if (globalStore.perfil?.obras_acesso === 1) {
            router.push('/main-obras');
        } else {
            router.push('/noroute');
        }

    } catch (error) {
        console.error('Erro leitura dos dados', error);
        errorMessage.value = `Não foi possível realizar o login: ${error.message}`;
    } finally {
        isLoading.value = false;
    }
};
</script>


<style scoped>
.login-page {
    min-height: 100vh;
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    background: #eef1f5;
}

.login-hero {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 48px;
    background:
        linear-gradient(135deg, rgba(5, 31, 78, 0.96), rgba(13, 75, 138, 0.92)),
        radial-gradient(circle at top left, rgba(255, 255, 255, 0.18), transparent 35%);
    color: #fff;
}

.hero-content {
    max-width: 560px;
    width: 100%;
    text-align: left;
}

.hero-brand {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
    margin-bottom: 28px;
}

.brand-logo {
    display: block;
    width: 190px;
    background: rgba(255, 255, 255, 0.96);
    padding: 14px 18px;
    border-radius: 16px;
    margin: 0;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.22);
}

.hero-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: fit-content;
    padding: 7px 14px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.14);
    border: 1px solid rgba(255, 255, 255, 0.24);
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 1px;
    margin: 0;
}

.hero-content h1 {
    margin: 0;
    font-size: 42px;
    line-height: 1.08;
    font-weight: 800;
}

.hero-content p {
    margin: 18px 0 0;
    font-size: 17px;
    line-height: 1.6;
    color: rgba(255, 255, 255, 0.84);
}

.hero-list {
    display: grid;
    gap: 10px;
    margin-top: 30px;
    font-size: 15px;
    color: rgba(255, 255, 255, 0.92);
}

.login-area {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
}

.login-card {
    width: 100%;
    max-width: 430px;
    background: #ffffff;
    border-radius: 24px;
    padding: 34px;
    box-shadow: 0 20px 60px rgba(15, 23, 42, 0.14);
    border: 1px solid rgba(148, 163, 184, 0.22);
}

.login-header {
    margin-bottom: 26px;
    text-align: left;
}

.login-header h2 {
    margin: 0;
    color: #102a56;
    font-size: 28px;
    font-weight: 800;
}

.login-header p {
    margin: 8px 0 0;
    color: #64748b;
    font-size: 14px;
}

.field-label {
    display: block;
    margin: 14px 0 7px;
    font-size: 13px;
    font-weight: 700;
    color: #334155;
    text-align: left;
}

.input-group {
    position: relative;
}

.input-group input {
    width: 100%;
    height: 48px;
    padding: 0 14px 0 44px;
    border: 1px solid #cbd5e1;
    border-radius: 13px;
    font-size: 15px;
    color: #0f172a;
    background: #f8fafc;
    transition: all 0.2s ease;
    box-sizing: border-box;
}

.input-group input:focus {
    outline: none;
    border-color: #1d4ed8;
    background: #ffffff;
    box-shadow: 0 0 0 4px rgba(29, 78, 216, 0.12);
}

.input-group .icon {
    position: absolute;
    left: 15px;
    top: 50%;
    width: 18px;
    height: 18px;
    transform: translateY(-50%);
    opacity: 0.62;
    pointer-events: none;
}

.robot-check {
    margin-top: 18px;
}

.login-button {
    width: 100%;
    height: 48px;
    margin-top: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    border: none;
    border-radius: 13px;
    background: linear-gradient(135deg, #102a56, #1d4ed8);
    color: #ffffff;
    font-size: 15px;
    font-weight: 800;
    cursor: pointer;
    transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
    box-shadow: 0 10px 24px rgba(29, 78, 216, 0.26);
}

.login-button:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 14px 28px rgba(29, 78, 216, 0.32);
}

.login-button:disabled {
    cursor: not-allowed;
    opacity: 0.72;
}

.loader {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.42);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.75s linear infinite;
}

.error-message {
    margin-top: 16px;
    padding: 11px 13px;
    border-radius: 12px;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #b91c1c;
    font-size: 13px;
    font-weight: 700;
    text-align: left;
}

.login-footer {
    margin-top: 24px;
    padding-top: 18px;
    border-top: 1px solid #e2e8f0;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    color: #64748b;
    font-size: 12px;
}

.login-footer small {
    color: #94a3b8;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

@media (max-width: 900px) {
    .login-page {
        grid-template-columns: 1fr;
    }

    .login-hero {
        display: none;
    }

    .login-area {
        min-height: 100vh;
        padding: 20px;
    }

    .login-card {
        padding: 26px;
        border-radius: 20px;
    }
}
</style>